import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, Users, Award, Briefcase, GraduationCap, 
         IndianRupee, CheckCircle, Calendar, MapPin, Sparkles,
         Play, ArrowRight, ArrowLeft, MessageCircle } from 'lucide-react';
import hiringPartnersGrid from '../assets/hiring-partners-grid.jpg';
import { extractVideoId, createEmbedUrl, getThumbnailUrl } from '../utils/youtubeUtils';
import YouTubeVideo from '../components/YouTubeVideo';

// Import success stories images
import truptiImg from '../assets/success stories/TruptiNagar_Microsoft.png';
import sachinImg from '../assets/success stories/SachinTomar_CityBank.png';
import shubhamJainImg from '../assets/success stories/ShubhamJain_NTT Data.png';
import neerajImg from '../assets/success stories/NeerajSharma_HCL.png';
import rakshitImg from '../assets/success stories/RakshitBhatia_JDSoftware.png';

// Import all placement images
import vasuImg from '../assets/placements/Vasu-Tiwari-Innoeye.jpg';
import tanwantImg from '../assets/placements/Tanwant-Singh-WorldPay.jpg';
import shubhamImg from '../assets/placements/Shubham-Rathore.jpg';
import shivaImg from '../assets/placements/Shiva-Jain-Hotwax.jpg';
import shailendraImg from '../assets/placements/Shailendra-MishraNew-Con-infosystems (1).jpg';
import ruchikaImg from '../assets/placements/Ruchika-Khandelwal-Innoeye.jpg';
import prachiImg from '../assets/placements/Prachi-Bundela-Fitstlogic.jpg';
import niteshImg from '../assets/placements/Nitesh-Choyal-systemetrix.jpg';
import nileshImg from '../assets/placements/Nilesh-Goyal-Hotwax.jpg';
import niketImg from '../assets/placements/Niket-Thada-Hotwax.jpg';
import nehaImg from '../assets/placements/Neha-Parsai-Synechrone (1).jpg';
import manishImg from '../assets/placements/Manish-Singh-New-Con-infosystems.jpg';
import manaliImg from '../assets/placements/Manali-Jain-Diaspark (1).jpg';
import lokeshImg from '../assets/placements/Lokesh-Sharma-infobeans (1).jpg';
import lkshyajeetImg from '../assets/placements/Lkshyajeet-Singh-Yash-technology.jpg';
import krishnaImg from '../assets/placements/Krishna-Patel-Firstlogic.jpg';
import indrajeetImg from '../assets/placements/IndraJeet-Singh-Infobeans.jpg';
import hemantImg from '../assets/placements/Hemant-Jhanwar-Diaspark.jpg';
import hardikImg from '../assets/placements/Hardik-Jain-New-Con.jpg';
import ektaImg from '../assets/placements/Ekta-Sharma--Synechrome.jpg';
import aprnaImg from '../assets/placements/Aprna-Katare-Synechrone.jpg';
import ankushImg from '../assets/placements/Ankush-Shukla-Synechrome.jpg';
import ankitImg from '../assets/placements/Ankit-Joshi-Hotwax.jpg';
import ajayImg from '../assets/placements/Ajay-Riyar-Worldpay.jpg';

// Import placement drive images
import synechron1 from '../assets/placement-drives/Synechron/synechron-drive-2024-1.jpg';
import synechron2 from '../assets/placement-drives/Synechron/synechron-drive-2024-2.jpg';
import synechron3 from '../assets/placement-drives/Synechron/synechron-drive-2024-3.jpg';
import synechron4 from '../assets/placement-drives/Synechron/synechron-drive-2024-4.jpg';
import synechron5 from '../assets/placement-drives/Synechron/synechron-drive-2024-5.jpg';
import synechron6 from '../assets/placement-drives/Synechron/synechron-drive-2024-6.jpg';
import synechron7 from '../assets/placement-drives/Synechron/synechron-drive-2024-7.jpg';
import synechron8 from '../assets/placement-drives/Synechron/synechron-drive-2024-8.jpg';
import hotwax1 from '../assets/placement-drives/HotWax/hotwax-drive-2024-1.jpg';
import hotwax2 from '../assets/placement-drives/HotWax/hotwax-drive-2024-2.jpg';
import hotwax3 from '../assets/placement-drives/HotWax/hotwax-drive-2024-3.jpg';
import hotwax4 from '../assets/placement-drives/HotWax/hotwax-drive-2024-4.jpg';
import hotwax5 from '../assets/placement-drives/HotWax/hotwax-drive-2024-5.jpg';

import worldpay1 from '../assets/placement-drives/WorldPay/worldpay-drive-2024-1.jpg';
import worldpay2 from '../assets/placement-drives/WorldPay/worldpay-drive-2024-2.jpg';
import worldpay3 from '../assets/placement-drives/WorldPay/worldpay-drive-2024-3.jpg';
import worldpay4 from '../assets/placement-drives/WorldPay/worldpay-drive-2024-4.jpg';
import worldpay5 from '../assets/placement-drives/WorldPay/worldpay-drive-2024-5.jpg';

const Placement = () => {
  const [visiblePlacements, setVisiblePlacements] = useState(15);
  const [videoModal, setVideoModal] = useState({ isOpen: false, videoUrl: '', studentName: '', company: '' });

  const placements = [
    {
      name: "Vasu Tiwari",
      role: "Software Developer",
      company: "Rakuten Symphony",
      image: vasuImg
    },
    {
      name: "Tanwant Singh",
      role: "Java Developer",
      company: "WorldPay",
      image: tanwantImg
    },
    {
      name: "Shubham Rathore",
      role: "Full Stack Developer",
      company: "Rakuten Symphony",
      image: shubhamImg
    },
    {
      name: "Shiva Jain",
      role: "Software Engineer",
      company: "Hotwax",
      image: shivaImg
    },
    {
      name: "Shailendra Mishra",
      role: "Java Developer",
      company: "HVantage",
      image: shailendraImg
    },
    {
      name: "Ruchika Khandelwal",
      role: "Software Developer",
      company: "Innoeye",
      image: ruchikaImg
    },
    {
      name: "Prachi Bundela",
      role: "Web Developer",
      company: "Firstlogic",
      image: prachiImg
    },
    {
      name: "Nitesh Choyal",
      role: "Software Engineer",
      company: "Systemetrix",
      image: niteshImg
    },
    {
      name: "Nilesh Goyal",
      role: "Full Stack Developer",
      company: "Hotwax",
      image: nileshImg
    },
    {
      name: "Niket Thada",
      role: "Software Developer",
      company: "Hotwax",
      image: niketImg
    },
    {
      name: "Neha Parsai",
      role: "Java Developer",
      company: "Synechron",
      image: nehaImg
    },
    {
      name: "Manish Singh",
      role: "Software Engineer",
      company: "HVantage",
      image: manishImg
    },
    {
      name: "Manali Jain",
      role: "Data Analyst",
      company: "Diaspark",
      image: manaliImg
    },
    {
      name: "Lokesh Sharma",
      role: "Java Developer",
      company: "Infobeans",
       image: lokeshImg
    },
    {
      name: "Lkshyajeet Singh",
      role: "Software Developer",
      company: "Yash Technology",
      image: lkshyajeetImg
    },
    {
      name: "Krishna Patel",
      role: "Web Developer",
      company: "Firstlogic",
     image: krishnaImg
    },
    {
      name: "IndraJeet Singh",
      role: "Software Engineer",
      company: "Infobeans",
       image: indrajeetImg
    },
    {
      name: "Hemant Jhanwar",
      role: "Java Developer",
      company: "Diaspark",
       image: hemantImg
    },
    {
      name: "Hardik Jain",
      role: "Software Developer",
      company: "HVantage",
      image: hardikImg
    },
    {
      name: "Ekta Sharma",
      role: "Full Stack Developer",
      company: "Synechron",
      image: ektaImg
    },
    {
      name: "Aprna Katare",
      role: "Software Engineer",
      company: "Synechron",
       image: aprnaImg
    },
    {
      name: "Ankush Shukla",
      role: "Java Development",
      company: "Synechron",
      image: ankushImg
    },
    {
      name: "Ankit Joshi",
      role: "Software Developer",
      company: "Hotwax",
      image: ankitImg
    },
    {
      name: "Ajay Riyar",
      role: "Java Developer",
      company: "WorldPay",
      
      image: ajayImg
    }
  ];

  // Success Stories Data
  const successStories = [
    {
      name: "Trupti Nagar",
      role: "Software Engineer",
      company: "Microsoft, USA",
      image: truptiImg || '/src/assets/success stories/TruptiNagar_Microsoft.png'
    },
    {
      name: "Sachin Tomar",
      role: "Software Developer",
      company: "City Bank",
      image: sachinImg || '/src/assets/success stories/SachinTomar_CityBank.png'
    },
    {
      name: "Shubham Jain",
      role: "Software Engineer",
      company: "NTT Data",
      image: shubhamJainImg || '/src/assets/success stories/ShubhamJain_NTT Data.png'
    },
    {
      name: "Neeraj Sharma",
      role: "Software Developer",
      company: "HCL, Netherland",
      image: neerajImg || '/src/assets/success stories/NeerajSharma_HCL.png'
    },
    {
      name: "Rakshit Bhatia",
      role: "Software Engineer",
      company: "JD Software",
    
      image: rakshitImg || '/src/assets/success stories/RakshitBhatia_JDSoftware.png'
    }
  ];

  // Function to get YouTube thumbnail URL
  const getYouTubeThumbnail = useCallback((videoUrl) => {
    if (videoUrl === "#") return null;
    const videoId = extractVideoId(videoUrl);
    return videoId ? getThumbnailUrl(videoId) : null;
  }, []);

  // Video modal functions
  const openVideoModal = useCallback((testimonial) => {
    if (testimonial.videoUrl !== "#") {
      // Convert YouTube URL to embeddable format
      const videoId = extractVideoId(testimonial.videoUrl);
      const embedUrl = videoId ? createEmbedUrl(videoId) : testimonial.videoUrl;
      
      setVideoModal({
        isOpen: true,
        videoUrl: embedUrl,
        studentName: testimonial.name,
        company: testimonial.company
      });
    }
  }, []);

  const closeVideoModal = useCallback(() => {
    setVideoModal({ isOpen: false, videoUrl: '', studentName: '', company: '' });
  }, []);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && videoModal.isOpen) {
        closeVideoModal();
      }
    };

    if (videoModal.isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [videoModal.isOpen, closeVideoModal]);

  const videoTestimonials = useMemo(() => [
    {
      name: "Trupti Nagar Jain",
      company: "Microsoft, USA",
      videoUrl: "https://youtu.be/ZmID8vdF6c8?si=UU_e1K2dKWGfMAsK",
      thumbnail: getYouTubeThumbnail("https://youtu.be/ZmID8vdF6c8?si=UU_e1K2dKWGfMAsK")
    },
    {
      name: "Sachin Tomar",
      company: "City Bank, Pune",
      videoUrl: "https://www.youtube.com/watch?v=EZROGHX2TCY",
      thumbnail: getYouTubeThumbnail("https://www.youtube.com/watch?v=EZROGHX2TCY")
    },
    {
      name: "Anshul Gupta",
      company: "Wipro, Mumbai",
      videoUrl: "https://www.youtube.com/watch?v=pEvczGfvs9k&t=1s",
      thumbnail: getYouTubeThumbnail("https://www.youtube.com/watch?v=pEvczGfvs9k&t=1s")
    },
    {
      name: "Neeraj Sharma",
      company: "HCL, Netherland",
      videoUrl: "https://youtu.be/q0i7v0ZjmBA?si=_xbZ592JX8kZcZvT",
      thumbnail: getYouTubeThumbnail("https://youtu.be/q0i7v0ZjmBA?si=_xbZ592JX8kZcZvT")
    },
    {
      name: "Shubham Jain",
      company: "NTT Data, USA",
      videoUrl: "https://youtu.be/OYIiAdhd-Ec?si=0npSzWhmBihZRjul",
      thumbnail: getYouTubeThumbnail("https://youtu.be/OYIiAdhd-Ec?si=0npSzWhmBihZRjul")
    },
    {
      name: "Rakshit Bhatia",
      company: "JD Software, Banglore",
      videoUrl: "https://youtu.be/TAHnPRA-ghA?si=3Tndt4e2WxkyIAbx",
      thumbnail: getYouTubeThumbnail("https://youtu.be/TAHnPRA-ghA?si=3Tndt4e2WxkyIAbx")
    },
    {
      name: "Shiva Jain",
      company: "TCS, Indore",
      videoUrl: "https://youtu.be/CiXi9FTXnnA?si=fJhDAeqCfTJmAuCA",
      thumbnail: getYouTubeThumbnail("https://youtu.be/CiXi9FTXnnA?si=fJhDAeqCfTJmAuCA")
    }
  ], [getYouTubeThumbnail]);

  const placementDrives = [
    {
      company: "Synechron Technologies, Pune",
      title: "HR Interview & Assessment",
      description: "HR interview rounds and final selection process with company executives.",
      images: [synechron1, synechron2, synechron3, synechron4],
      slides: [
    
        {
          image: synechron2,
          title: "Welcome Synechron @ OSOP!!",
          description: "We're thrilled to have you at OSOP Center Indore.",
          highlights: ["Coding Round", "Technical MCQs", "System Design Questions"]
        },
        {
          image: synechron3,
          title: "Technical Assessment",
          description: "Rigorous technical evaluation covering programming concepts and problem-solving skills.",
          highlights: ["Students Selected", "Competitive Package", "Indore Location"]
        },
        {
          image: synechron4,
          title: "Welcome Sarosh Raghwan, Global Head, Recruitement, Synechron",
          description: "Welcome Sarosh Raghwan by Bajpai Sir",
          highlights: ["Students Selected", "Competitive Package", "Indore Location"]
        },
        
          {
          image: synechron6,
          title: "Final Selection",
          description: "Successful completion of the placement drive with outstanding selection results.",
          highlights: ["Students Selected", "Competitive Package", "Pune Location"]
        },
          {
          image: synechron7,
          title: "Welcome Synechron's Technical Managers",
          description: "OSOP Welcome all Technical Managers who came for Tech Assessment",
          highlights: ["Students Selected", "Competitive Package", "Indore Location"]
        },
          {
          image: synechron8,
          title: "HR Interview Process",
          description: "Face-to-face interviews with Synechron HR Head.",
          highlights: ["Behavioral Assessment", "Technical Discussion", "Cultural Fit Evaluation"]
          
        }
      ],
    },
    {
      company: "WorldPay Global",
      title: "Campus Recruitment Drive",
      description: "Full-day recruitment process including aptitude, technical, and HR rounds.",
      images: [worldpay1, worldpay2, worldpay3, worldpay4, worldpay5],
      slides: [
        {
          image: worldpay5,
          title: "Welcome Wordpay Team @ OSOP",
          description: "OSOP Welcome Worldpay Team for Campus Hiring.",
          highlights: ["Students Selected", "Competitive Package", "Global Exposure Opportunity"]
        },
        {
          image: worldpay1,
          title: "Technical Assesment by Worldpay",
          description: "Technical Assessment Exam Conducted by Worldpay Team.",
          highlights: ["Professional Setup", "Multiple Interview Panels", "Technology Infrastructure"]
        },
        {
          image: worldpay2,
          title: "Worldpay & OSOP Team",
          description: "Group Photograph of Worldpay & OSOP Team after a successful campus drive.",
          highlights: ["Company Culture", "Global Opportunities", "Technology Stack"]
        },
        {
          image: worldpay3,
          title: "Welcome Worldpay's Technical Managers",
          description: "OSOP Welcome all Technical Managers who came for Tech Assessment",
          highlights: ["Technical Questions", "Problem Solving", "Analytical Reasoning"]
        },
        {
          image: worldpay4,
          title: "Welcome Worldpay's Technical Managers",
          description: "OSOP Welcome Worldpay's Technical Managers who came for Tech Assessment",
          highlights: ["Technical Expertise", "Project Discussion", "Behavioral Assessment"]
        }
        
      ],
      details: {
        roles: ["Assistant System Engineer", "Digital Associate"],
        package: "Competitive",
        locations: ["Indore"],
        studentsPlaced: 0
      }
    },
    {
      company: "HotWax Systems",
      title: "Technical Assessment Drive",
      description: "Comprehensive technical evaluation and coding assessment for multiple roles.",
      images: [hotwax1, hotwax2, hotwax3, hotwax4, hotwax5],
      slides: [
        {
          image: hotwax1,
          title: "Welcome Hotwax Tech Managers",
          description: "OSOP Welcome Hotwax Tech Managers.",
          highlights: ["Company Overview", "Role Descriptions", "Career Growth Path"]
        },
        {
          image: hotwax2,
          title: "Aptitude Test",
          description: "Comprehensive aptitude assessment covering logical reasoning and quantitative skills.",
          highlights: ["Logical Reasoning", "Quantitative Aptitude", "English Proficiency"]
        },
        {
          image: hotwax3,
          title: "Technical Round",
          description: "Candidates are waiting for the Technical Assessment.",
          highlights: ["Live Coding", "Database Queries", "Framework Knowledge"]
        },
        {
          image: hotwax4,
          title: "Technical Exam",
          description: "Hotwax team conducted Technical Exam @ OSOP.",
          highlights: ["Communication Skills", "Leadership Qualities", "Team Dynamics"]
        },
        {
          image: hotwax5,
          title: "Welcome Mr. Ashish Vijayvargiya",
          description: "OSOP Welcome Mr. Ashish Vijayvargiya, India Head, Hotwax.",
          highlights: ["Students Placed", "Competitive Package", "Immediate Joining"]
        }
      ],
      details: {
        roles: ["System Engineer", "Specialist Programmer", "Senior Associate"],
        package: "Competitive",
        locations: ["Indore"],
        studentsPlaced: 0
      }
    },
    
  ];

  const loadMore = () => {
    setVisiblePlacements(prev => Math.min(prev + 15, placements.length));
  };

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 pt-20 overflow-hidden">
      {/* Enhanced Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-professional opacity-95"></div>
        <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
        
        <div className="container-max relative z-10 py-20">
          <div className="text-center text-white">
          <motion.div
              className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            >
              <Award className="w-5 h-5 mr-2 text-yellow-400" />
              <span className="text-sm font-medium">OSOP's Placement Activities</span>
            </motion.div>

            <motion.h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight px-4 sm:px-0"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Get Trained{' '}
              <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                Get Placed
              </span>
            </motion.h1>

            <motion.p 
              className="text-base sm:text-lg md:text-xl lg:text-2xl mb-8 sm:mb-12 max-w-4xl mx-auto leading-relaxed text-gray-100 px-4 sm:px-0"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              OSOP's Placement Cell provided more than 500 direct placement opportunities via conducting OnCampus Placement Drives for different IT MNCs. 15000+ Candidates got placed from their college or in scheduled interview process.  
            </motion.p>

            {/* Quick Stats */}
          <motion.div
              className="flex flex-col md:flex-row justify-center items-center gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto px-4 sm:px-0"
              initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
          >
              <div className="bg-white/10 backdrop-blur-sm p-4 sm:p-6 rounded-2xl border border-white/20 text-center hover:bg-white/20 transition-all duration-300 w-full md:w-1/3">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-yellow-400 mb-2">26+</div>
                <div className="text-gray-200 text-sm sm:text-base">Years Of Training & Consulting</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 sm:p-6 rounded-2xl border border-white/20 text-center hover:bg-white/20 transition-all duration-300 w-full md:w-1/3">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-400 mb-2">15K+</div>
                <div className="text-gray-200 text-sm sm:text-base">Students Trained</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 sm:p-6 rounded-2xl border border-white/20 text-center hover:bg-white/20 transition-all duration-300 w-full md:w-1/3">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-purple-400 mb-2">100%</div>
                <div className="text-gray-200 text-sm sm:text-base">Satisfied Learners</div>
              </div>
            </motion.div>
            </div>
        </div>
      </section>

      <div className="container-max py-12 sm:py-16 md:py-20 space-y-16 sm:space-y-24 md:space-y-32">

        {/* Past Placement Drives */}
        <section>
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 px-4 sm:px-0">
              OSOP's onCampus Placement Drives
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              First Ever Institute in Central India conducted OnCampus Placement Drives with leading MNCs
            </p>
          </motion.div>

        {/* Individual Company Drive Sections */}
        <div className="space-y-16">
          {placementDrives && placementDrives.length > 0 ? (
            placementDrives.map((drive, driveIndex) => (
            <motion.div
              key={driveIndex}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: driveIndex * 0.2 }}
              className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-soft-lg border border-gray-100 dark:border-gray-700 overflow-hidden"
            >
            {/* Company Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4 sm:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="flex items-center">
                  <Building className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold leading-tight break-words">
                        {drive.company}
                    </h3>
                    <p className="text-primary-100 mt-1 text-sm sm:text-base">
                      Campus Placement Drive
                    </p>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold">
                    Placement Drive
                  </div>
                  <div className="text-primary-100 text-xs sm:text-sm">Successful Event</div>
                </div>
              </div>
            </div>

              {/* Drive Content */}
              <div className="p-4 sm:p-6 md:p-8">
                {drive.slides && drive.slides.length > 0 ? (
                  <div className="space-y-8">
                    {/* Main Image Gallery */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {drive.slides && drive.slides.map((slide, slideIndex) => (
            <motion.div
                          key={slideIndex}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.6, delay: slideIndex * 0.1 }}
                          className="group cursor-pointer"
                        >
                          <div className="relative overflow-hidden rounded-2xl shadow-lg">
                            <img 
                              src={slide.image}
                              alt={`${drive.company} - ${slide.title}`}
                              className="w-full h-48 sm:h-56 md:h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="absolute bottom-4 left-4 right-4 text-white">
                                <h4 className="text-lg font-bold mb-2">{slide.title}</h4>
                                <p className="text-sm text-gray-200 line-clamp-2">{slide.description}</p>
                    </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                  </div>

                    {/* Drive Details */}
                    <div className="grid lg:grid-cols-2 gap-8">
                      {/* Process Overview */}
                  {/* <div className="space-y-6">
                        <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                          Drive Process Overview
                      </h4>
                        <div className="space-y-4">
                          {drive.slides && drive.slides.map((slide, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.6, delay: index * 0.1 }}
                              className="flex items-start space-x-4"
                            >
                              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                <span className="text-sm font-bold">{index + 1}</span>
                              </div>
                              <div>
                                <h5 className="font-semibold text-gray-900 dark:text-white mb-1">
                                  {slide.title}
                                </h5>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                  {slide.description}
                                </p>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div> */}

                      {/* Drive Highlights */}
                      {/* <div className="space-y-6">
                        <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                          Drive Highlights
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {drive.highlights && drive.highlights.map((highlight, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.6, delay: index * 0.1 }}
                              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                            >
                              <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                                {highlight.title}
                              </h5>
                              <p className="text-gray-600 dark:text-gray-400 text-sm">
                                {highlight.description}
                              </p>
                            </motion.div>
                          ))}
                        </div>
                      </div> */}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">No drive details available</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No placement drives available</p>
            </div>
          )}
        </div>
        </section>

        {/* Real Feedback Section */}
        <section className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/10 dark:to-primary-800/10 py-20 rounded-3xl">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 px-4 sm:px-0">
              Success Stories
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              Watch, How OSOPCoding Helped Students in Cracking their Dream IT MNC
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {videoTestimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 h-full flex flex-col cursor-pointer"
                  onClick={() => openVideoModal(testimonial)}
                >
                  <div className="relative aspect-video bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex-shrink-0 overflow-hidden">
                    {/* Video Thumbnail */}
                    {testimonial.thumbnail ? (
                      <img 
                        src={testimonial.thumbnail} 
                        alt={`${testimonial.name} testimonial thumbnail`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to gradient background if thumbnail fails to load
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-primary-600/20 rounded-full flex items-center justify-center mb-2 mx-auto">
                            <Play className="w-6 h-6 text-primary-600" />
                          </div>
                          <p className="text-sm text-primary-600 font-medium">Video Coming Soon</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="w-16 h-16 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-110 shadow-2xl">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </div>
                    

                    
                    {/* Duration overlay */}
                    {testimonial.videoUrl !== "#" && (
                      <div className="absolute bottom-3 right-3 bg-black/80 text-white px-2 py-1 rounded text-xs font-medium">
                        Video
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex-grow flex flex-col justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1">
                        {testimonial.name}
                      </h3>
                      <p className="text-primary-600 dark:text-primary-400 text-sm font-medium mb-3 line-clamp-1">
                        {testimonial.company}
                      </p>
                    </div>

                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
        <section>
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 px-4 sm:px-0">
              Our Alumni
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              Meet our successful alumni who have secured good positions at leading companies across world
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {/* Combine success stories and placements, with success stories first */}
            {[...successStories, ...placements.slice(0, visiblePlacements)].map((item, index) => (
              <motion.div
                key={`combined-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 overflow-hidden group hover:-translate-y-2"
              >
                <div className="relative overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        className="w-full h-48 sm:h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                                                                onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                      />
                      {/* Fallback for broken images */}
                      <div className="hidden w-full h-48 sm:h-56 md:h-64 bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                        <div className="text-gray-500 dark:text-gray-400 text-center">
                          <div className="w-16 h-16 bg-gray-300 dark:bg-gray-500 rounded-full flex items-center justify-center mx-auto mb-2">
                            <span className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                              {item.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <span className="text-sm">{item.name}</span>
                        </div>
                      </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300 line-clamp-1">
                    {item.name}
                  </h3>
                  <div className="space-y-2">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Building className="w-3 h-3 mr-1 text-primary-500 flex-shrink-0" />
                      <span className="font-medium text-xs line-clamp-1">{item.company}</span>
                  </div>
                  {/* <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <MapPin className="w-3 h-3 mr-1 text-primary-500 flex-shrink-0" />
                      <span className="text-xs line-clamp-1">{item.location}</span>
                    </div> */}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {visiblePlacements < placements.length && (
            <div className="text-center mt-12">
              <motion.button
                onClick={loadMore}
                className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Users className="w-6 h-6 mr-3" />
                All Alumni
                <ArrowRight className="w-6 h-6 ml-3" />
              </motion.button>
            </div>
          )}
      </section>

      {/* Hiring Partners */}
        <section className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/10 dark:to-primary-800/10 py-20 rounded-3xl">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 px-4 sm:px-0">
              Our Students Placed in Top MNCs 
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              100% of our Students get placed with top IT MNCs from thier college or off campus. 400+ Direct placement by OSOPCoding with different IT MNCs
            </p>
          </motion.div>
          
          <motion.div
            className="text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src={hiringPartnersGrid} 
              alt="Hiring Partners" 
              className="mx-auto rounded-2xl shadow-2xl max-w-full h-auto"
            />
          </motion.div>
        </section>

        {/* Call to Action */}
        <section className="text-center bg-gradient-professional p-16 rounded-3xl shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Join Our Success Stories?
              </h2>
              <p className="text-xl text-gray-100 mb-10 max-w-3xl mx-auto leading-relaxed">
                Take the first step towards your dream career. Join OSOP today and become part of our successful placement legacy.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-6">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <a 
                    href="/courses" 
                    className="bg-white text-primary-600 hover:bg-gray-100 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center transition-all duration-300"
                  >
                    <GraduationCap className="w-6 h-6 mr-3" />
                    Explore Courses
                    <ArrowRight className="w-6 h-6 ml-3" />
                  </a>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <a 
                    href="/enquire" 
                    className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center transition-all duration-300"
                  >
                    <Sparkles className="w-6 h-6 mr-3" />
                    Start Your Journey
                    <ArrowRight className="w-6 h-6 ml-3" />
                  </a>
                </motion.div>
              </div>
          </motion.div>
        </div>
      </section>
      </div>

      {/* Video Modal */}
      {videoModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10010] flex items-center justify-center p-4" style={{ paddingTop: '3rem' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-2xl w-full h-full max-w-6xl max-h-[85vh] flex flex-col"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {videoModal.studentName}
                </h3>
                <p className="text-primary-600 dark:text-primary-400 font-medium text-sm sm:text-base">
                  {videoModal.company}
                </p>
              </div>
              <button
                onClick={closeVideoModal}
                className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                aria-label="Close video"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Video Container - Flexible height */}
            <div className="flex-1 bg-black min-h-0 flex items-center justify-center">
              <div className="w-full h-full flex items-center justify-center">
                <YouTubeVideo
                  videoUrl={videoModal.videoUrl}
                  title={`${videoModal.studentName} - ${videoModal.company} Testimonial`}
                  className="w-full h-full max-w-full max-h-full"
                  style={{ minHeight: '300px' }}
                  fallbackMessage={`Watch ${videoModal.studentName}'s testimonial on YouTube`}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Real feedback from our successful student
                </div>
                <button
                  onClick={closeVideoModal}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

    </div>
  );
};

export default Placement; 